import {
  format,
  formatDistanceToNow,
  isMatch,
  isValid,
  Locale,
  intlFormat,
  IntlFormatFormatOptions,
  parse,
  parseISO,
} from 'date-fns';
import { enCA, frCA } from 'date-fns/locale';

import { Locales } from '@/store/language.interface';
import { getLocale } from '@/utils/data-display-utils';

function diffInDays(start: number | string | Date, end: number | string | Date): number {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffInMilliseconds = endDate.valueOf() - startDate.valueOf();
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
  return Math.round(diffInDays);
}

function formatDate(date: number, languageEnabled: Locales[] | undefined = []): string {
  let dateFormat: string;
  let locale: Locale;
  switch (getLocale(languageEnabled)) {
    case 'en':
      dateFormat = 'yyyy MMMM d';
      locale = enCA;
      break;
    case 'fr':
    default:
      dateFormat = 'd MMMM yyyy';
      locale = frCA;
      break;
  }
  return format(new Date(date * 1000), dateFormat, { locale });
}

function formatDistanceDateToNow(date: Date, languageEnabled: Locales[] | undefined = []): string {
  const locale = (() => {
    switch (getLocale(languageEnabled)) {
      case 'en':
        return enCA;
      case 'fr':
      default:
        return frCA;
    }
  })();
  return formatDistanceToNow(date, { locale });
}

function getCurrentYear(): number {
  return new Date().getFullYear();
}

function todayInTimestamp(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * Formats a Date object into a localized string based on the provided options and language preferences.
 *
 * This function uses the Intl.DateTimeFormat API under the hood to ensure proper localization.
 * It supports formatting dates, times, or both, and respects the user's enabled locales.
 *
 * @param date - The Date object to format.
 * @param languageEnabled - An optional array of enabled locales (e.g., ['en', 'fr']).
 *                          If provided, the best matching locale will be used.
 *                          Defaults to an empty array (falls back to browser default).
 * @param options - Formatting options for Intl.DateTimeFormat.
 *                  Common examples:
 *                  - { dateStyle: 'long' } → "December 22, 2025"
 *                  - { hour: 'numeric', minute: '2-digit', hourCycle: 'h12' } → "2:30 PM"
 *                  - { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' } → "Mon 22 Dec 2025"
 *                  Defaults to { dateStyle: 'long' }.
 *
 * @returns A formatted date/time string in the appropriate locale.
 *
 * @example
 * // Full date
 * formatDateCustom(new Date(), ['en-GB'], { dateStyle: 'full' });
 * // → "Monday, 22 December 2025"
 *
 * @example
 * // Time in 12-hour format with AM/PM
 * formatDateCustom(new Date(), ['en-US'], { hour: 'numeric', minute: '2-digit', hourCycle: 'h12' });
 * // → "3:45 PM"
 */
function formatDateCustom(
  date: Date,
  languageEnabled: Locales[] | undefined = [],
  options: IntlFormatFormatOptions = { dateStyle: 'long' },
): string {
  return intlFormat(date, options, { locale: getLocale(languageEnabled) });
}

function parseDateInput(value: Date | string): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  if (isMatch(trimmedValue, 'yyyy-MM-dd')) {
    const parsedDate = parse(trimmedValue, 'yyyy-MM-dd', new Date());
    return isValid(parsedDate) ? parsedDate : null;
  }

  const isoDate = parseISO(trimmedValue);
  if (isValid(isoDate)) return isoDate;

  const fallbackDate = new Date(trimmedValue);
  return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
}

function formatMediumDate(date: Date | string, languageEnabled: Locales[] | undefined = []): string {
  const normalizedDate = parseDateInput(date);
  if (!normalizedDate) return '';

  return formatDateCustom(normalizedDate, languageEnabled, { dateStyle: 'medium' });
}

/**
 * Formats only the time portion of a date
 *
 * @param date - The Date object to extract and format the time from.
 * @param languageEnabled - Optional array of enabled locales for proper localization.
 * @param useLeadingZero - If true, displays hour with leading zero (e.g., "02:30 PM" instead of "2:30 PM").
 *                         Defaults to false.
 *
 * @returns A localized time string (e.g., "2:30 PM" or "14:30" depending on locale).
 *
 * @example
 * formatTimeCustom(new Date('2025-12-22T14:30:00'), ['en-US'], true);
 * // → "02:30 PM"
 */
function formatTimeCustom(
  date: Date,
  languageEnabled: Locales[] | undefined = [],
  useLeadingZero: boolean = false,
): string {
  return formatDateCustom(date, languageEnabled, {
    hour: useLeadingZero ? '2-digit' : 'numeric',
    minute: '2-digit',
  });
}

export {
  diffInDays,
  formatDate,
  formatDistanceDateToNow,
  getCurrentYear,
  todayInTimestamp,
  formatDateCustom,
  parseDateInput,
  formatMediumDate,
  formatTimeCustom,
};
